import styles from './testPlanModal.module.scss';
import { useTranslation } from "react-i18next";
import { Input } from "../../shared/components/input/input";
import {
  useCreateTestPlanMutation,
  useDeleteTestPlanMutation,
  useUpdateTestPlanMutation
} from "../../api/testPlanService";
import {
  useCallback,
  useEffect,
  useState
} from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  number,
  object,
  string
} from "yup";
import { TestPlan } from "../../types/testPlanTypes";
import { MessageWindow } from "../../shared/components/messageWindow/messageWindow";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../store";
import { setUpdateModal } from "../../store/slices/mainSlice";
import { ModalWindow } from "../../shared/components/modalWindow/modalWindow";
import { TextArea } from "../../shared/components/textArea/textArea";

type AddTestCaseProps = {
  onClose: (isSave: boolean, idCreate?: number) => void;
  testPlan?: TestPlan
}

export const TestPlanModal = ({ onClose, testPlan }: AddTestCaseProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>()

  const [showDeleteMessage, setShowDeleteMessage] = useState<boolean>(false);

  const [showConfirmCancel, setShowConfirmCancel] = useState(false);

  const [createTestPlan, { data: createData, isSuccess: isSuccessCreateTestPlan, isLoading: isLoadingCreate }] = useCreateTestPlanMutation();
  const [updateTestPlan, { isSuccess: isSuccessUpdateTestPlan, isLoading: isLoadingUpdate }] = useUpdateTestPlanMutation();
  const [deleteTestPlan, { isSuccess: isSuccessDeleteTestPlan, isLoading: isLoadingDelete }] = useDeleteTestPlanMutation();

  useEffect(() => {
    if (isSuccessCreateTestPlan && createData) {
      dispatch(setUpdateModal({ show: true, isDelete: false }));
      onClose(true, createData);
    }
  }, [isSuccessCreateTestPlan]);

  useEffect(() => {
    if (isSuccessUpdateTestPlan) {
      dispatch(setUpdateModal({ show: true, isDelete: false }));
      onClose(true);
    }
  }, [isSuccessUpdateTestPlan]);

  useEffect(() => {
    if (isSuccessDeleteTestPlan) {
      dispatch(setUpdateModal({ show: true, isDelete: true }));
      onClose(true);
    }
  }, [isSuccessDeleteTestPlan]);


  const schema = object({
    name: string().required(t("common.notValid")).typeError(t("common.notValid")),
    maximumExecutionTimeout: number().required(t("common.notValid")).typeError(t("common.notValid"))
      .max(10000, t("common.notValid")).min(1, t("common.notValid")),
    description: string(),
  });

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty }
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: testPlan?.name || "",
      maximumExecutionTimeout: testPlan?.maximumExecutionTimeout || 0,
      description: testPlan?.description || ""
    },
    mode: "onChange"
  });

  const onCancel = useCallback(() => {
    if (isDirty) {
      setShowConfirmCancel(true);
    } else {
      onClose(false);
    }
  }, [isDirty, onClose]);

  const closeConfirmCancelModal = useCallback((isConfirm: boolean) => {
    if (isConfirm) {
      onClose(false);
    }
    setShowConfirmCancel(false);
  }, [setShowConfirmCancel]);


  const onClickSave = (): void => {
    handleSubmit((value) => {
      const newTestPlan: TestPlan = {
        description: value.description,
        name: value.name,
        maximumExecutionTimeout: value.maximumExecutionTimeout,
        assignedScenarios: []
      };
      if (!!testPlan) {
        newTestPlan.id = testPlan.id;
        updateTestPlan(newTestPlan);
      } else {
        createTestPlan(newTestPlan);
      }
    })();
  }

  const onDeleteClick = (): void => {
    setShowDeleteMessage(true);
  }

  const closeMessageWindow = (isConfirm: boolean): void => {
    if (isConfirm && !!testPlan?.id) {
      deleteTestPlan(testPlan.id);
    }
    setShowDeleteMessage(false);
  }

  return (
    <>
      <ModalWindow onClose={onCancel}
                   width={380}
                   title={!!testPlan ? t("testPlan.edit") : t("testPlan.add")}
                   children={
                     <div className={styles.container}>
                       <div className={styles.inputContainer}>
                         <span className={styles.required}>{t("common.name")}</span>
                         <div className={styles.input}>
                           <Input control={control}
                                  name={"name"}
                                  maxLength={100}
                                  error={!!errors.name ? errors.name.message : ""}/>
                         </div>
                       </div>
                       <div className={styles.maxExecutionInputContainer}>
                         <span className={styles.required}>{t("common.maxExecutionTimeout")}</span>
                         <div className={styles.input}>
                           <Input control={control}
                                  name={"maximumExecutionTimeout"}
                                  maxLength={4}
                                  type={"number"}
                                  error={!!errors.maximumExecutionTimeout ? errors.maximumExecutionTimeout.message : ""}/>
                         </div>
                       </div>
                       <div className={styles.descriptionContainer}>
                         <span>{t("common.description")}</span>
                         <TextArea control={control}
                                   name={"description"}
                                   maxLength={57000}/>
                       </div>
                       <div className={styles.buttonsContainer}>
                         <div className={styles.buttons}>
                           {!!testPlan && (
                             <button className={styles.delete}
                                     disabled={isLoadingCreate || isLoadingDelete || isLoadingUpdate}
                                     onClick={onDeleteClick}>{t("common.delete")}</button>
                           )}
                         </div>
                         <div className={styles.buttons}>
                           <button className={styles.cancel}
                                   disabled={isLoadingCreate || isLoadingDelete || isLoadingUpdate}
                                   onClick={onCancel}>{t("common.cancel")}</button>
                           <button className={styles.save}
                                   disabled={isLoadingCreate || isLoadingDelete || isLoadingUpdate}
                                   onClick={onClickSave}>{t("common.save")}</button>
                         </div>
                       </div>
                     </div>
                   }
      />

      {showDeleteMessage && (
        <MessageWindow onClose={closeMessageWindow}
                       title={t("testPlan.delete")}
                       description={t("testPlan.deleteDescription")}
                       primaryText={t("common.ok")} secondaryText={t("common.cancel")}/>
      )}

      {showConfirmCancel && (
        <MessageWindow onClose={closeConfirmCancelModal}
                       title={t("common.cancelChanges")}
                       description={t("common.cancelChangesDescription")}
                       primaryText={t("common.ok")} secondaryText={t("common.cancel")}/>
      )}
    </>

  );
}